import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const TITLE = "Football Rules for Beginners: A Simple Guide | Sportara";
const DESCRIPTION =
  "New to football? Learn the basic rules, scoring, field positions, and gameplay in this beginner-friendly guide from Sportara.";
const CANONICAL = "https://sportaraapp.com/guides/football-rules-beginners";

const FAQS = [
  {
    question: "What is a down in football?",
    answer:
      "A down is one play or attempt. The offense gets four downs to move the ball 10 yards. If they succeed, they earn a new set of four downs. If they fail, possession goes to the other team.",
  },
  {
    question: "How many points is a touchdown worth?",
    answer:
      "A touchdown is worth 6 points. After scoring a touchdown, the team can attempt an extra point (1 point) or a two-point conversion (2 points).",
  },
  {
    question: "What happens if the offense doesn't get a first down?",
    answer:
      "If the offense fails to move the ball 10 yards in four downs, they must turn the ball over to the other team. On fourth down, teams usually punt the ball away or attempt a field goal instead of risking a turnover.",
  },
  {
    question: "How many players are on the field for each team?",
    answer:
      "Each team has 11 players on the field at once — one offense versus one defense. Special teams units also have 11 players during kicks and punts.",
  },
  {
    question: "How long does a football game last?",
    answer:
      "A regulation game has four 15-minute quarters, totaling 60 minutes of game clock. With stoppages, halftime, and timeouts, most games last about 3 hours.",
  },
  {
    question: "What's the difference between a field goal and an extra point?",
    answer:
      "An extra point is a short kick attempted right after a touchdown (usually from the 15-yard line) and is worth 1 point. A field goal is a kick from anywhere on the field during regular play and is worth 3 points.",
  },
  {
    question: "What is pass interference?",
    answer:
      "Pass interference happens when a defender makes illegal contact with a receiver before the ball arrives — such as grabbing, pushing, or blocking the receiver's path. It results in a penalty and an automatic first down for the offense.",
  },
  {
    question: "Can a football game end in a tie?",
    answer:
      "In the NFL regular season, yes — if the score is tied after one overtime period. In the playoffs, teams play additional overtime periods until a winner is decided. College football uses different overtime rules with each team getting a possession.",
  },
];

function setMeta(name: string, content: string, attr: "name" | "property" = "name") {
  let el = document.head.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setCanonical(href: string) {
  let el = document.head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function setPreferredSport() {
  try {
    localStorage.setItem("preferredSport", "football");
  } catch {
    // ignore
  }
}

export default function FootballRulesBeginners() {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = TITLE;
    setMeta("description", DESCRIPTION);
    setCanonical(CANONICAL);
    setMeta("og:title", TITLE, "property");
    setMeta("og:description", DESCRIPTION, "property");
    setMeta("og:url", CANONICAL, "property");
    setMeta("og:type", "article", "property");
    setMeta("twitter:card", "summary_large_image");

    const articleLd = {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: "Football Rules for Beginners: A Simple Guide",
      description: DESCRIPTION,
      url: CANONICAL,
      datePublished: "2026-06-10",
      dateModified: "2026-06-10",
      author: { "@type": "Organization", name: "Sportara" },
      publisher: {
        "@type": "Organization",
        name: "Sportara",
        logo: { "@type": "ImageObject", url: "https://sportaraapp.com/logo.png" },
      },
      mainEntityOfPage: { "@type": "WebPage", "@id": CANONICAL },
    };

    const faqLd = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: FAQS.map((faq) => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: faq.answer,
        },
      })),
    };

    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.text = JSON.stringify({ "@graph": [articleLd, faqLd] });
    document.head.appendChild(ld);

    return () => {
      document.title = prevTitle;
      ld.remove();
    };
  }, []);

  return (
    <main className="min-h-screen bg-background text-foreground">
      <div className="max-w-3xl mx-auto px-5 py-8">
        <Link to="/">
          <Button variant="ghost" size="icon" aria-label="Go back" className="mb-4">
            <ArrowLeft className="w-5 h-5" />
          </Button>
        </Link>

        <article className="space-y-6">
          <header className="space-y-3">
            <p className="text-sm text-primary font-semibold uppercase tracking-wider">
              Beginner Guide
            </p>
            <h1 className="font-display text-4xl md:text-5xl font-bold leading-tight">
              Football Rules for Beginners: A Simple Guide
            </h1>
            <p className="text-lg text-muted-foreground">
              Just getting into football? Here's a friendly overview of how the game works —
              scoring, positions, and the basic rules — written for total newcomers.
            </p>
          </header>

          <section className="space-y-3">
            <h2 className="font-display text-2xl font-bold">The Goal of the Game</h2>
            <p className="text-muted-foreground leading-relaxed">
              American football is played between two teams of 11 players each. The team with
              the ball (the offense) tries to move it down a 100-yard field and score, while
              the other team (the defense) tries to stop them. Whoever has the most points
              when time runs out wins.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="font-display text-2xl font-bold">How Scoring Works</h2>
            <ul className="space-y-2 text-muted-foreground leading-relaxed list-disc pl-5">
              <li><strong className="text-foreground">Touchdown (6 points)</strong> — carry or catch the ball in the opponent's end zone.</li>
              <li><strong className="text-foreground">Extra Point (1)</strong> or <strong className="text-foreground">Two-Point Conversion (2)</strong> — bonus play right after a touchdown.</li>
              <li><strong className="text-foreground">Field Goal (3 points)</strong> — kick the ball through the uprights.</li>
              <li><strong className="text-foreground">Safety (2 points)</strong> — defense tackles an offensive player in their own end zone.</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="font-display text-2xl font-bold">The Field</h2>
            <p className="text-muted-foreground leading-relaxed">
              The field is 100 yards long with a 10-yard end zone at each end. Yard lines run
              every 5 yards, and the 50-yard line splits the field in half. The offense moves
              the ball toward the opponent's end zone to score.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="font-display text-2xl font-bold">Downs: The Core Rule</h2>
            <p className="text-muted-foreground leading-relaxed">
              The offense has <strong className="text-foreground">four attempts (downs)</strong> to
              move the ball 10 yards. If they succeed, they get a fresh set of four downs. If
              they don't, the other team takes over. That's why you'll hear announcers say
              things like "3rd and 7" — third attempt, 7 yards still to go.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="font-display text-2xl font-bold">Key Positions</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="rounded-xl border border-border p-4">
                <h3 className="font-semibold mb-1">Quarterback (QB)</h3>
                <p className="text-sm text-muted-foreground">Leader of the offense. Throws or hands off the ball.</p>
              </div>
              <div className="rounded-xl border border-border p-4">
                <h3 className="font-semibold mb-1">Running Back (RB)</h3>
                <p className="text-sm text-muted-foreground">Carries the ball on running plays.</p>
              </div>
              <div className="rounded-xl border border-border p-4">
                <h3 className="font-semibold mb-1">Wide Receiver (WR)</h3>
                <p className="text-sm text-muted-foreground">Catches passes from the quarterback.</p>
              </div>
              <div className="rounded-xl border border-border p-4">
                <h3 className="font-semibold mb-1">Offensive Line</h3>
                <p className="text-sm text-muted-foreground">Blocks defenders to protect the QB and open running lanes.</p>
              </div>
              <div className="rounded-xl border border-border p-4">
                <h3 className="font-semibold mb-1">Defensive Line & Linebackers</h3>
                <p className="text-sm text-muted-foreground">Stop the run and pressure the quarterback.</p>
              </div>
              <div className="rounded-xl border border-border p-4">
                <h3 className="font-semibold mb-1">Cornerbacks & Safeties</h3>
                <p className="text-sm text-muted-foreground">Cover receivers and prevent big passing plays.</p>
              </div>
            </div>
          </section>

          <section className="space-y-3">
            <h2 className="font-display text-2xl font-bold">Game Length</h2>
            <p className="text-muted-foreground leading-relaxed">
              A game has four 15-minute quarters with a halftime break. If the score is tied,
              teams play overtime. The clock stops on incomplete passes, when a player runs
              out of bounds, and at the two-minute warning of each half.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="font-display text-2xl font-bold">Common Penalties</h2>
            <ul className="space-y-2 text-muted-foreground leading-relaxed list-disc pl-5">
              <li><strong className="text-foreground">Offsides / False Start</strong> — moving across the line before the snap.</li>
              <li><strong className="text-foreground">Holding</strong> — illegally grabbing a player.</li>
              <li><strong className="text-foreground">Pass Interference</strong> — making contact with a receiver before the ball arrives.</li>
              <li><strong className="text-foreground">Face Mask</strong> — grabbing another player's face mask.</li>
            </ul>
          </section>

          {/* FAQ Section */}
          <section className="space-y-4 pt-4">
            <h2 className="font-display text-2xl font-bold">Frequently Asked Questions</h2>
            <Accordion type="single" collapsible className="w-full">
              {FAQS.map((faq, i) => (
                <AccordionItem key={i} value={`faq-${i}`}>
                  <AccordionTrigger className="text-left font-display font-semibold text-base">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>

          <section className="rounded-2xl border border-primary/30 bg-primary/5 p-6 space-y-3">
            <h2 className="font-display text-2xl font-bold">Ready to Level Up?</h2>
            <p className="text-muted-foreground">
              Sportara turns rules like these into quick, gamified drills that build real Game IQ — perfect for brand-new players, parents, and coaches.
            </p>
            <Link to="/auth/signup" onClick={setPreferredSport}>
              <Button size="lg" className="mt-2">Start Learning Free</Button>
            </Link>
          </section>
        </article>
      </div>
    </main>
  );
}
